import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  Bell,
  Boxes,
  Building2,
  CheckCheck,
  ClipboardCheck,
  ClipboardList,
  Coins,
  Gauge,
  Globe,
  LayoutGrid,
  LifeBuoy,
  LogOut,
  Megaphone,
  Menu,
  MessageSquareWarning,
  Network,
  Percent,
  Receipt,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Store,
  Tags,
  Truck,
  UserCog,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth, useSession, type OrgType } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { relative } from '../lib/format';
import { cx, StatusPill } from './ui';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perm?: string;
  badge?: number;
}
interface NavGroup {
  label: string;
  items: NavItem[];
}

function useNav(type: OrgType, active: boolean): NavGroup[] {
  const { t } = useI18n();
  const ic = (C: typeof Gauge) => <C className="size-[17px]" aria-hidden />;
  const counts = useQuery({
    queryKey: ['nav-counts', type],
    queryFn: async () => {
      if (type === 'BUYER') {
        const [approvals, cart] = await Promise.all([api.get('/orders', { awaitingMyApproval: true, pageSize: 1 }), api.get('/cart')]);
        return { approvals: approvals.total as number, cart: cart.itemCount as number };
      }
      if (type === 'SUPPLIER') {
        const o = await api.get('/orders', { status: 'PLACED', pageSize: 1 });
        return { newOrders: o.total as number };
      }
      const k = await api.get('/admin/kyc', { pageSize: 1 }).catch(() => ({ total: 0 }));
      const m = await api.get('/admin/products', { pageSize: 1 }).catch(() => ({ total: 0 }));
      return { kyc: k.total as number, moderation: m.total as number };
    },
    enabled: active,
    refetchInterval: 60_000,
  });
  const c = (counts.data ?? {}) as Record<string, number | undefined>;
  if (!active && type !== 'PLATFORM') {
    return [
      { label: t('section.account'), items: [{ to: '/onboarding', label: t('nav.onboarding'), icon: ic(ClipboardCheck) }, { to: '/company', label: t('nav.company'), icon: ic(Building2) }, { to: '/support', label: t('nav.support'), icon: ic(LifeBuoy) }] },
    ];
  }
  if (type === 'BUYER')
    return [
      {
        label: t('section.buy'),
        items: [
          { to: '/', label: t('nav.dashboard'), icon: ic(Gauge) },
          { to: '/catalog', label: t('nav.catalog'), icon: ic(LayoutGrid), perm: 'buyer.catalog.browse' },
          { to: '/suppliers', label: t('nav.suppliers'), icon: ic(Store), perm: 'buyer.catalog.browse' },
          { to: '/rfqs', label: t('nav.rfqs'), icon: ic(ClipboardList) },
          { to: '/cart', label: t('nav.cart'), icon: ic(ShoppingCart), perm: 'buyer.cart.manage', badge: c.cart },
        ],
      },
      {
        label: t('section.operate'),
        items: [
          { to: '/orders', label: t('nav.orders'), icon: ic(Truck) },
          { to: '/approvals', label: t('nav.approvals'), icon: ic(CheckCheck), perm: 'buyer.order.approve', badge: c.approvals },
          { to: '/invoices', label: t('nav.invoices'), icon: ic(Receipt), perm: 'buyer.invoice.view' },
          { to: '/disputes', label: t('nav.disputes'), icon: ic(MessageSquareWarning) },
        ],
      },
      { label: t('section.account'), items: [{ to: '/company', label: t('nav.company'), icon: ic(Building2) }, { to: '/support', label: t('nav.support'), icon: ic(LifeBuoy) }] },
    ];
  if (type === 'SUPPLIER')
    return [
      {
        label: t('section.sell'),
        items: [
          { to: '/', label: t('nav.dashboard'), icon: ic(Gauge) },
          { to: '/supplier/products', label: t('nav.products'), icon: ic(Boxes) },
          { to: '/rfqs', label: t('nav.rfqs'), icon: ic(ClipboardList) },
          { to: '/orders', label: t('nav.orders'), icon: ic(Truck), badge: c.newOrders },
        ],
      },
      {
        label: t('section.money'),
        items: [
          { to: '/invoices', label: t('nav.invoices'), icon: ic(Receipt), perm: 'supplier.invoice.view' },
          { to: '/supplier/finance', label: t('nav.finance'), icon: ic(Wallet), perm: 'supplier.settlement.view' },
          { to: '/disputes', label: t('nav.disputes'), icon: ic(MessageSquareWarning) },
        ],
      },
      { label: t('section.account'), items: [{ to: '/company', label: t('nav.company'), icon: ic(Building2) }, { to: '/support', label: t('nav.support'), icon: ic(LifeBuoy) }] },
    ];
  return [
    {
      label: t('section.operate'),
      items: [
        { to: '/', label: t('nav.dashboard'), icon: ic(Gauge), perm: 'platform.dashboard.view' },
        { to: '/admin/kyc', label: t('nav.kyc'), icon: ic(ShieldCheck), perm: 'platform.kyc.decide', badge: c.kyc },
        { to: '/admin/organizations', label: t('nav.organizations'), icon: ic(Building2), perm: 'platform.orgs.manage' },
        { to: '/admin/moderation', label: t('nav.moderation'), icon: ic(BadgeCheck), perm: 'platform.catalog.moderate', badge: c.moderation },
        { to: '/orders', label: t('nav.orders'), icon: ic(Truck), perm: 'platform.orders.view' },
        { to: '/rfqs', label: t('nav.rfqs'), icon: ic(ClipboardList), perm: 'platform.orders.view' },
        { to: '/disputes', label: t('nav.disputes'), icon: ic(MessageSquareWarning), perm: 'platform.disputes.manage' },
        { to: '/support', label: t('nav.support'), icon: ic(LifeBuoy), perm: 'platform.support.manage' },
      ],
    },
    {
      label: t('section.money'),
      items: [
        { to: '/admin/payments', label: t('nav.payments'), icon: ic(Coins), perm: 'platform.finance.manage' },
        { to: '/admin/settlements', label: t('nav.settlements'), icon: ic(Wallet), perm: 'platform.finance.manage' },
        { to: '/admin/commissions', label: t('nav.commissions'), icon: ic(Percent), perm: 'platform.finance.manage' },
        { to: '/invoices', label: t('nav.invoices'), icon: ic(Receipt), perm: 'platform.orders.view' },
      ],
    },
    {
      label: t('section.platform'),
      items: [
        { to: '/admin/categories', label: t('nav.categories'), icon: ic(Tags), perm: 'platform.categories.manage' },
        { to: '/admin/users', label: t('nav.users'), icon: ic(Users), perm: 'platform.users.manage' },
        { to: '/admin/content', label: t('nav.content'), icon: ic(Megaphone), perm: 'platform.content.manage' },
        { to: '/admin/audit', label: t('nav.audit'), icon: ic(ScrollText), perm: 'platform.audit.view' },
        { to: '/admin/configuration', label: t('nav.configuration'), icon: ic(SlidersHorizontal), perm: 'platform.settings.manage' },
        { to: '/admin/integrations', label: t('nav.integrations'), icon: ic(Network), perm: 'platform.settings.manage' },
        { to: '/company', label: t('nav.company'), icon: ic(Building2), perm: 'platform.users.manage' },
      ],
    },
  ];
}

const ORG_ACCENT: Record<OrgType, string> = { BUYER: 'bg-basil', SUPPLIER: 'bg-slate', PLATFORM: 'bg-ink' };
const ORG_LABEL: Record<OrgType, string> = { BUYER: 'Buyer', SUPPLIER: 'Supplier', PLATFORM: 'Operator' };

export function AppShell() {
  const { org, user, can, memberships, signOut } = useSession();
  const { refresh } = useAuth();
  const { t, locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const active = org.status === 'ACTIVE' || org.type === 'PLATFORM';
  const nav = useNav(org.type, active);
  useEffect(() => setOpen(false), [location.pathname]);

  const switchOrg = async (orgId: string) => {
    await api.post('/auth/switch-org', { orgId });
    await refresh();
    window.location.href = '/';
  };

  const sidebar = (
    <nav className="flex h-full flex-col" aria-label="Main">
      <Link to="/" className="flex items-center gap-2.5 px-4 pt-4 pb-3">
        <img src="/favicon.svg" alt="" className="size-7" />
        <span className="cond text-[17px] font-semibold tracking-tight">HORECA Connect</span>
      </Link>
      <div className="mx-3 mb-3 rounded-md border border-line bg-paper px-3 py-2">
        <div className="flex items-center gap-2">
          <span className={cx('size-2 rounded-full', ORG_ACCENT[org.type])} aria-hidden />
          <span className="truncate text-[13px] font-medium">{org.displayName}</span>
        </div>
        <div className="mt-0.5 flex items-center justify-between text-[12px] text-ink-3">
          <span>{ORG_LABEL[org.type]} account</span>
          {org.status !== 'ACTIVE' ? <StatusPill status={org.status} /> : <span className="num">{org.code}</span>}
        </div>
        {memberships.length > 1 && (
          <select className="mt-2 w-full rounded border border-line bg-surface px-1.5 py-1 text-[12.5px]" value={org.id} onChange={(e) => switchOrg(e.target.value)} aria-label="Switch organisation">
            {memberships.map((m) => (
              <option key={m.orgId} value={m.orgId}>
                {m.orgName}
              </option>
            ))}
          </select>
        )}
      </div>
      <div className="flex-1 space-y-4 overflow-y-auto px-2 pb-4">
        {nav.map((g) => {
          const items = g.items.filter((i) => !i.perm || can(i.perm));
          if (!items.length) return null;
          return (
            <div key={g.label}>
              <div className="px-2.5 pb-1 text-[12px] text-ink-3">{g.label}</div>
              <ul>
                {items.map((i) => (
                  <li key={i.to}>
                    <NavLink
                      to={i.to}
                      end={i.to === '/'}
                      className={({ isActive }) =>
                        cx('flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13.5px]', isActive ? 'bg-basil-tint font-medium text-basil-dark' : 'text-ink-2 hover:bg-sunken hover:text-ink')
                      }
                    >
                      {i.icon}
                      <span className="flex-1">{i.label}</span>
                      {!!i.badge && <span className="num rounded-full bg-saffron px-1.5 text-[11px] font-semibold text-white">{i.badge}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="border-t border-line p-2">
        <NavLink to="/settings" className="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[13px] hover:bg-sunken">
          <span className="flex size-7 items-center justify-center rounded-full bg-sunken text-[12px] font-semibold text-ink-2">{initials(user.fullName)}</span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{user.fullName}</span>
            <span className="block truncate text-[12px] text-ink-3">{user.email}</span>
          </span>
          <Settings className="size-4 text-ink-3" aria-hidden />
        </NavLink>
        <div className="mt-1 flex gap-1">
          <button className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[12.5px] text-ink-2 hover:bg-sunken" onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}>
            <Globe className="size-3.5" /> {t('common.language')}
          </button>
          <button className="flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[12.5px] text-ink-2 hover:bg-sunken" onClick={signOut}>
            <LogOut className="size-3.5 rtl:rotate-180" /> {t('common.signOut')}
          </button>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[248px_1fr]">
      <div className="hidden border-e border-line bg-surface lg:block">
        <aside className="sticky top-0 h-screen">{sidebar}</aside>
      </div>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-ink/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 start-0 w-[270px] bg-surface shadow-xl">
            <button className="absolute end-2 top-3 rounded p-1.5 hover:bg-sunken" onClick={() => setOpen(false)} aria-label="Close menu">
              <X className="size-4" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="flex min-w-0 flex-col">
        <TopBar onMenu={() => setOpen(true)} />
        <main className="mx-auto w-full max-w-[1320px] flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { org, can } = useSession();
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const showSearch = org.type === 'BUYER' && org.status === 'ACTIVE' && can('buyer.catalog.browse');
  return (
    <div className="no-print sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-line bg-surface/95 px-4 backdrop-blur sm:px-6 lg:px-8">
      <button className="rounded p-1.5 hover:bg-sunken lg:hidden" onClick={onMenu} aria-label="Open menu">
        <Menu className="size-5" />
      </button>
      {showSearch ? (
        <form
          className="relative max-w-xl flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            nav(`/catalog?q=${encodeURIComponent(q)}`);
          }}
          role="search"
        >
          <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-3" aria-hidden />
          <input className="field ps-8!" placeholder="Search products, brands or suppliers" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search catalogue" />
        </form>
      ) : (
        <div className="flex-1" />
      )}
      <div className="ms-auto flex items-center gap-1">
        {org.type === 'BUYER' && org.status === 'ACTIVE' && (
          <Link to="/cart" className="rounded-md p-2 text-ink-2 hover:bg-sunken" aria-label="Cart">
            <ShoppingCart className="size-5" />
          </Link>
        )}
        <NotificationBell />
        <Link to="/settings" className="rounded-md p-2 text-ink-2 hover:bg-sunken" aria-label="Settings">
          <UserCog className="size-5" />
        </Link>
      </div>
    </div>
  );
}

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const nav = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useI18n();
  const q = useQuery({ queryKey: ['notifications'], queryFn: () => api.get('/notifications', { pageSize: 12 }), refetchInterval: 30_000 });
  const read = useMutation({
    mutationFn: (ids: string[] | 'all') => api.post('/notifications/read', { ids }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
  useEffect(() => {
    const h = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const unread = q.data?.unread ?? 0;
  return (
    <div className="relative" ref={ref}>
      <button className="relative rounded-md p-2 text-ink-2 hover:bg-sunken" onClick={() => setOpen((o) => !o)} aria-label={`${t('common.notifications')} (${unread} unread)`} aria-expanded={open}>
        <Bell className="size-5" />
        {unread > 0 && <span className="num absolute end-1 top-1 min-w-4 rounded-full bg-saffron px-1 text-[10px] leading-4 font-semibold text-white">{unread > 99 ? '99+' : unread}</span>}
      </button>
      {open && (
        <div className="absolute end-0 mt-1 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-line bg-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <span className="font-semibold">{t('common.notifications')}</span>
            {unread > 0 && (
              <button className="text-[12.5px] text-basil hover:underline" onClick={() => read.mutate('all')}>
                {t('common.markAllRead')}
              </button>
            )}
          </div>
          <ul className="max-h-[420px] overflow-y-auto">
            {(q.data?.items ?? []).length === 0 && <li className="px-4 py-8 text-center text-[13px] text-ink-3">You're all caught up.</li>}
            {(q.data?.items ?? []).map((n: any) => (
              <li key={n.id}>
                <button
                  className={cx('block w-full border-b border-line px-4 py-3 text-start hover:bg-sunken/60', !n.readAt && 'bg-basil-tint/40')}
                  onClick={() => {
                    if (!n.readAt) read.mutate([n.id]);
                    setOpen(false);
                    if (n.link) nav(n.link);
                  }}
                >
                  <div className="flex items-start gap-2">
                    {!n.readAt && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-saffron" aria-label="Unread" />}
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-medium">{n.title}</div>
                      <div className="line-clamp-2 text-[12.5px] text-ink-2">{n.body}</div>
                      <div className="mt-0.5 text-[11.5px] text-ink-3">{relative(n.createdAt)}</div>
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
          <Link to="/notifications" onClick={() => setOpen(false)} className="block px-4 py-2.5 text-center text-[13px] text-basil hover:bg-sunken">
            View all
          </Link>
        </div>
      )}
    </div>
  );
}

export const initials = (n: string) =>
  n
    .split(/\s+/)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

